import React, { useState, useEffect } from 'react';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkingMode } from '../../../contexts/WorkingModeContext';
import apiService from '../../../services/api';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import { GlobalLeaderboardEntry, Group, FilterState, LeaderboardStatsData } from './types';
import LeaderboardStats from './components/LeaderboardStats';
import LeaderboardTable from './components/LeaderboardTable';
import LeaderboardTabs from './components/LeaderboardTabs';
import PersonalPositionCard from './components/PersonalPositionCard';
import BonusPointsModal from './components/BonusPointsModal';
import { UserPermission } from '../../../types';
import styles from '../../../styles/LeaderBoard.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import searchStyles from '../../../styles/SearchBar.module.css';
import paginationStyles from '../../../styles/Pagination.module.css';
import FloatingWorkingMode from '../../UI/FloatingWorkingMode';

const LeaderBoard: React.FC = () => {
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const { user } = useAuth();
  const { isWorkMode: contextWorkMode, setWorkMode } = useWorkingMode();

  const [leaderboard, setLeaderboard] = useState<GlobalLeaderboardEntry[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [hasViewOrEditPermissions, setHasViewOrEditPermissions] = useState(false);
  const [userPosition, setUserPosition] = useState<GlobalLeaderboardEntry | null>(null);
  const [showBonusPointsModal, setShowBonusPointsModal] = useState(false);
  
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    activeTab: 'overall',
    timePeriod: 'month',
    category: 'all', // Will be used for tab-based filtering
    sortBy: 'points',
    selectedGroups: [],
    pageSize: 20
  });

  const tabOptions = [
    { value: 'overall', label: 'Overall' },
    { value: 'weekly', label: 'Recent Activity' },
    { value: 'monthly', label: 'Current Period' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'quality', label: 'Quality' }
  ];

  const timePeriodOptions: DropdownOption[] = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ];

  const sortByOptions: DropdownOption[] = [
    { value: 'points', label: 'Total Points' },
    { value: 'tasks', label: 'Tasks Completed' },
    { value: 'quality', label: 'Quality Score' },
    { value: 'efficiency', label: 'Efficiency' },
    { value: 'streak', label: 'Current Streak' }
  ];

  const groupOptions: DropdownOption[] = [
    { value: 'all', label: 'All Groups' },
    ...groups.map(group => ({
      value: group.id,
      label: group.name
    }))
  ];

  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissionsLoading(false);
        return;
      }
      
      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          setUserPermissions(permissions);
          
          const viewOrEditActions = ['edit', 'update'];
          const hasPermission = permissions.some(
            perm => perm.module === 'leader_board' && viewOrEditActions.includes(perm.action)
          );
          setHasViewOrEditPermissions(hasPermission);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
        setUserPermissions([]);
        setHasViewOrEditPermissions(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  const shouldShowWorkingMode = hasViewOrEditPermissions || user?.role === 'admin';
  
  const isWorkMode = shouldShowWorkingMode ? contextWorkMode : false;

  useEffect(() => {
    if (!shouldShowWorkingMode && contextWorkMode) {
      setWorkMode(false);
    }
  }, [shouldShowWorkingMode, contextWorkMode, setWorkMode]);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, []);

  // Debounced leaderboard loading - includes search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadLeaderboard();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.activeTab, filters.timePeriod, filters.sortBy, filters.selectedGroups, filters.pageSize, filters.search, currentPage]);

  const loadGroups = async () => {
    try {
      const response = await apiService.getGroups();
      if (response.status === 'success') {
        setGroups(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading groups:', error);
      notificationService.error('Failed to load groups');
    }
  };

  const loadLeaderboard = async () => {
    if (isLoadingLeaderboard) return;

    setIsLoadingLeaderboard(true);
    setLoading(true);
    showLoader('Loading leaderboard...');
    
    try {
      // Determine period and sort_by based on active tab
      let period = filters.timePeriod;
      let sortBy = filters.sortBy;
      let tab = filters.activeTab;
      
      // Tab-based filtering
      if (tab === 'weekly') {
        period = 'week';
        sortBy = sortBy || 'tasks'; // Default to tasks for weekly
      } else if (tab === 'monthly') {
        period = 'month';
        sortBy = sortBy || 'tasks'; // Default to tasks for monthly
      } else if (tab === 'tasks') {
        sortBy = 'tasks';
      } else if (tab === 'quality') {
        sortBy = 'quality';
      } else if (tab === 'overall') {
        // Overall: sort by combined score (tasks + quality)
        sortBy = 'overall'; // Will be handled in backend
      }
      
      const response = await apiService.getGlobalLeaderboard({
        period: period,
        limit: filters.pageSize,
        page: currentPage,
        sort_by: sortBy,
        tab: tab, // Pass tab to backend
        search: filters.search || undefined, // Pass search to backend
        group_ids: filters.selectedGroups.length > 0 && filters.selectedGroups[0] !== 'all' 
          ? filters.selectedGroups.filter(id => id !== 'all')
          : undefined
      });

      if (response.status === 'success') {
        let entries = response.data || [];
        
        // Find current user's position in the leaderboard
        if (user) {
          const userEntry = entries.find((entry: GlobalLeaderboardEntry) => 
            entry.user.id === user.id || entry.user.email === user.email
          );
          setUserPosition(userEntry || null);
        }
        
        // Search is handled on backend, so entries are already filtered
        setLeaderboard(entries);
        
        // Update pagination state
        if (response.pagination) {
          setTotalPages(response.pagination.total_pages);
          setTotalCount(response.pagination.total_count);
          setCurrentPage(response.pagination.current_page);
        }
        
        // Highlight first row after loading
        setTimeout(() => {
          setHighlightedRow(0);
        }, 100);
      } else {
        notificationService.error(response.message || 'Failed to load leaderboard');
      }
    } catch (error: any) {
      console.error('Error loading leaderboard:', error);
      notificationService.error('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingLeaderboard(false);
      hideLoader();
    }
  };

  const handleTabChange = (tab: string) => {
    setFilters(prev => {
      // Auto-adjust sort_by based on tab
      let newSortBy = prev.sortBy;
      if (tab === 'tasks') {
        newSortBy = 'tasks';
      } else if (tab === 'quality') {
        newSortBy = 'quality';
      } else if (tab === 'overall') {
        newSortBy = 'overall';
      }
      
      return { ...prev, activeTab: tab, sortBy: newSortBy };
    });
    setCurrentPage(1);
    setHighlightedRow(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setHighlightedRow(null);
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (key: keyof FilterState, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Generate pagination pages
  const generatePaginationPages = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleRowHover = (index: number) => {
    setHighlightedRow(index);
  };

  const handleRowLeave = () => {
    setHighlightedRow(null);
  };

  // Calculate stats
  const stats: LeaderboardStatsData = {
    totalPoints: leaderboard.reduce((sum, entry) => sum + entry.points, 0),
    totalTasks: leaderboard.reduce((sum, entry) => sum + entry.tasks_completed, 0),
    avgCompletion: leaderboard.length > 0 ? Math.round(leaderboard.reduce((sum, entry) => sum + entry.participation_score, 0) / leaderboard.length) : 0,
    longestStreak: leaderboard.length > 0 ? Math.max(...leaderboard.map(entry => entry.current_streak || 0)) : 0,
    activeMembers: leaderboard.length,
    // Personal mode stats (from userPosition if available)
    userPoints: userPosition?.points || 0,
    userTasks: userPosition?.tasks_completed || 0,
    userCompletionRate: userPosition ? (userPosition.quality_score || userPosition.participation_score || 0) : 0,
    userStreak: userPosition?.current_streak || 0,
    userRank: userPosition?.rank
  };


  return (
    <div className={styles.leaderboardContainer}>
      {/* Fixed Header */}
      <div className={styles.fixedHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div className={styles.searchWrapper}>
              <div className={searchStyles.searchBar}>
                <input 
                  type="text" 
                  placeholder="Search by name, email, or group"
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <button type="submit">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className={styles.headerActions}>
              {isWorkMode && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={() => setShowBonusPointsModal(true)}
                  title="Add Bonus Points"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span className={styles.btnText}>Add Bonus Points</span>
                </button>
              )}
              {isWorkMode && (
                <CustomDropdown
                  options={groupOptions}
                  value={filters.selectedGroups.length === 0 || filters.selectedGroups.includes('all') ? 'all' : filters.selectedGroups}
                  onChange={(value) => {
                    const selectedValue = Array.isArray(value) ? value : [value];
                    if (selectedValue.includes('all') || selectedValue.length === 0) {
                      handleFilterChange('selectedGroups', []);
                    } else {
                      handleFilterChange('selectedGroups', selectedValue.filter(v => v !== 'all'));
                    }
                  }}
                  placeholder="All Groups"
                  size="sm"
                  variant="outlined"
                  className="filter"
                  multiple={true}
                />
              )}
              {filters.activeTab !== 'weekly' && filters.activeTab !== 'monthly' && (
              <CustomDropdown
                options={timePeriodOptions}
                value={filters.timePeriod}
                onChange={(value) => handleFilterChange('timePeriod', value as string)}
                placeholder="Time Period"
                size="sm"
                variant="outlined"
                className="filter"
              />
              )}
              {filters.activeTab !== 'tasks' && filters.activeTab !== 'quality' && filters.activeTab !== 'overall' && (
              <CustomDropdown
                options={sortByOptions}
                value={filters.sortBy}
                onChange={(value) => handleFilterChange('sortBy', value as string)}
                placeholder="Sort By"
                size="sm"
                variant="outlined"
                className="filter"
              />
              )}
              
              <button 
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={() => loadLeaderboard()}
                title="Refresh"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
                <span className={styles.btnText}>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.contentSection}>
        {/* Tabs */}
        <LeaderboardTabs
          tabs={tabOptions}
          activeTab={filters.activeTab}
          onTabChange={handleTabChange}
        />


        {/* Personal Position Card - Only in Personal Mode */}
        {!isWorkMode && userPosition && (
          <PersonalPositionCard userEntry={userPosition} totalCount={totalCount} />
        )}

        {/* Stats Grid */}
        {loading ? (
          <div className={styles.statsGrid}>
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className={styles.statCardSkeleton}>
                <div className={styles.skeletonStatIcon}></div>
                <div className={styles.skeletonStatNumber}></div>
                <div className={styles.skeletonStatLabel}></div>
              </div>
            ))}
          </div>
        ) : (
          <LeaderboardStats stats={stats} isWorkMode={isWorkMode} />
        )}

        {/* Leaderboard Table */}
        {loading ? (
          <div className={styles.leaderboardTableContainer}>
            <table className={styles.leaderboardTable}>
              <thead>
                <tr>
                  <th className={styles.rankColumn}>Rank</th>
                  <th className={styles.memberColumn}>Member</th>
                  <th className={styles.groupColumn}>Group</th>
                  <th className={styles.pointsColumn}>Points</th>
                  <th className={styles.tasksColumn}>Tasks</th>
                  <th className={styles.qualityColumn}>Quality</th>
                  <th className={styles.actionsColumn}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }, (_, index) => (
                  <tr key={index} className={styles.skeletonRow}>
                    <td className={styles.rankCell}>
                      <div className={styles.skeletonRankBadge}></div>
                    </td>
                    <td className={styles.memberCell}>
                      <div className={styles.skeletonMemberInfo}>
                        <div className={styles.skeletonMemberAvatar}></div>
                        <div className={styles.skeletonMemberDetails}>
                          <div className={styles.skeletonMemberName}></div>
                          <div className={styles.skeletonMemberRole}></div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.groupCell}>
                      <div className={styles.skeletonGroupInfo}>
                        <div className={styles.skeletonGroupIndicator}></div>
                        <div className={styles.skeletonGroupName}></div>
                      </div>
                    </td>
                    <td className={styles.pointsCell}>
                      <div className={styles.skeletonStatValue}></div>
                      <div className={styles.skeletonStatLabelSmall}></div>
                    </td>
                    <td className={styles.tasksCell}>
                      <div className={styles.skeletonStatValue}></div>
                      <div className={styles.skeletonStatLabelSmall}></div>
                    </td>
                    <td className={styles.qualityCell}>
                      <div className={styles.skeletonStatValue}></div>
                      <div className={styles.skeletonStatLabelSmall}></div>
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.skeletonActionButton}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <LeaderboardTable
              entries={leaderboard}
              highlightedRow={highlightedRow}
              onRowHover={handleRowHover}
              onRowLeave={handleRowLeave}
              currentUserId={user?.id}
              isWorkMode={isWorkMode}
            />

            {/* Pagination */}
            {leaderboard.length > 0 && (
              <div className={styles.paginationWrapper}>
                <div className={styles.paginationInfo}>
                  Showing {((currentPage - 1) * filters.pageSize) + 1} to {Math.min(currentPage * filters.pageSize, totalCount)} of {totalCount} members
                </div>
                <div className={paginationStyles.pagination}>
                  {generatePaginationPages().map((page, index) => (
                    <button
                      key={index}
                      className={`${paginationStyles.paginationItem} ${currentPage === page ? paginationStyles.active : ''}`}
                      onClick={() => typeof page === 'number' ? handlePageChange(page) : undefined}
                      disabled={page === '...'}
                      style={page === '...' ? { cursor: 'default', opacity: 0.5 } : {}}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {shouldShowWorkingMode && (
        <FloatingWorkingMode
          position="bottom-right"
          activeLabel="Work Mode"
          inactiveLabel="Personal"
        />
      )}
      <BonusPointsModal
        isOpen={showBonusPointsModal}
        onClose={() => setShowBonusPointsModal(false)}
        onSuccess={() => {
          loadLeaderboard();
        }}
      />
    </div>
  );
};

export default LeaderBoard;

